import { useEffect, useState } from "react";
import { api } from "../../services/api.js";
import { useMediaQuery } from "react-responsive";
import { useClient } from "../../context/ClientContext.js";
import { Divider, Input, Spin, Tag } from "antd";
import { SearchOutlined, DownOutlined, CloseOutlined } from "@ant-design/icons";

const capitalize = (text = "") => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  const clean = hex.replace("#", "");
  const full = clean.length === 3 ? clean.split("").map(c => c + c).join("") : clean;
  if (full.length !== 6) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const pastelize = (hex, amount = 0.86) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#f3f4f6";
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
};

export const FiltrosCatalogo = ({ categoriaIds = [], onSelect, noBorder = false, onClose }) => {
  const { theme, config } = useClient();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRoots, setOpenRoots] = useState([]);
  const [q, setQ] = useState("");

  const isMobile = useMediaQuery({ query: `(max-width: 882px)` });

  useEffect(() => {
    setLoading(true);
    api.catalogo.getCategorias()
      .then(res => setCats(res?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (cats.length === 0) return;
    const parentIds = cats
      .filter(c => categoriaIds.includes(c.id) && c.id_parent)
      .map(c => c.id_parent);
    if (parentIds.length > 0) {
      setOpenRoots(prev => [...new Set([...prev, ...parentIds])]);
    }
  }, [cats, categoriaIds]);

  if (!loading && cats.length === 0) return null;

  const filtered = cats.filter(c =>
    !q || c.nombre.toLowerCase().includes(q.toLowerCase())
  );

  const roots = cats.filter(c => !c.id_parent);
  const children = (parentId) => cats.filter(c => c.id_parent === parentId);
  const activeCats = cats.filter(c => categoriaIds.includes(c.id));
  const primary = theme.primaryColor || "#111";
  const badgeBg = pastelize(primary);
  const textColor = theme.textColor || "#202124";
  const fontFamily = config?.uiConfig?.t4?.brand?.font || "inherit";

  const toggleRoot = (id) => {
    setOpenRoots(prev => prev.includes(id) ? prev.filter(rootId => rootId !== id) : [...prev, id]);
  };

  const handleToggle = (id) => {
    onSelect(categoriaIds.includes(id)
      ? categoriaIds.filter(catId => catId !== id)
      : [...categoriaIds, id]
    );
  };

  return (
    <aside
      style={{
        width: isMobile ? "100%" : 300,
        flexShrink: 0,
        background: "#fff",
        borderRight: noBorder || isMobile ? 0 : "1px solid #ececec",
        borderTop: noBorder ? 0 : "1px solid #ececec",
        borderBottom: noBorder ? 0 : "1px solid #ececec",
        color: textColor,
        fontFamily,
      }}
    >
      <div style={{
        minHeight: 56,
        padding: "0 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #e7e7e7",
      }}>
        <h2
          style={{
            margin: 0,
            color: textColor,
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Filtros
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {categoriaIds.length > 0 && (
            <button
              type="button"
              onClick={() => onSelect([])}
              style={{
                border: 0,
                background: "transparent",
                color: primary,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              Limpiar
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                border: 0,
                background: "transparent",
                color: "#9ca3af",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CloseOutlined style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      </div>

      {activeCats.length > 0 && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "12px 14px" }}>
            {activeCats.map(cat => (
              <Tag
                key={cat.id}
                closable
                onClose={() => handleToggle(cat.id)}
                style={{
                  backgroundColor: badgeBg,
                  color: primary,
                  borderColor: pastelize(primary, 0.65),
                  margin: 0,
                  fontWeight: 600,
                }}
              >
                {capitalize(cat.nombre)}
              </Tag>
            ))}
          </div>
          <Divider style={{ margin: 0 }} />
        </>
      )}

      <form onSubmit={e => { e.preventDefault(); setQ(e.target.q.value); }} style={{ padding: "12px 16px 4px" }}>
        <Input
          name="q"
          size="middle"
          placeholder="Buscar en filtros"
          prefix={<SearchOutlined />}
          allowClear
          onChange={e => setQ(e.target.value)}
        />
      </form>

      <div style={{ maxHeight: isMobile ? "none" : "calc(100vh - 220px)", overflowY: "auto", padding: "8px 0 14px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}><Spin size="small" /></div>
        ) : q && filtered.length === 0 ? (
          <p style={{ margin: "12px 18px", color: "#999", fontSize: 14 }}>Sin resultados</p>
        ) : q ? (
          filtered.map(cat => (
            <CatItem
              key={cat.id}
              cat={cat}
              active={categoriaIds.includes(cat.id)}
              onToggle={handleToggle}
              theme={theme}
              indent={!!cat.id_parent}
            />
          ))
        ) : (
          roots.map(root => {
            const subs = children(root.id);
            const rootActive = categoriaIds.includes(root.id);
            const subActive = subs.some(s => categoriaIds.includes(s.id));
            const expanded = openRoots.includes(root.id) || rootActive || subActive;

            return (
              <div key={root.id}>
                <button
                  type="button"
                  onClick={() => subs.length > 0 ? toggleRoot(root.id) : handleToggle(root.id)}
                  style={{
                    width: "100%",
                    minHeight: 56,
                    border: 0,
                    background: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 16px",
                    color: rootActive || subActive ? primary : textColor,
                    fontSize: 15,
                    fontWeight: rootActive || subActive ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                  }}
                >
                  <span onClick={(e) => { e.stopPropagation(); handleToggle(root.id); }}>
                    {capitalize(root.nombre)}
                  </span>
                  {subs.length > 0 && (
                    <DownOutlined
                      style={{
                        fontSize: 14,
                        color: "#4b5563",
                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  )}
                </button>
                {expanded && subs.map(sub => (
                  <CatItem
                    key={sub.id}
                    cat={sub}
                    active={categoriaIds.includes(sub.id)}
                    onToggle={handleToggle}
                    theme={theme}
                    indent
                  />
                ))}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

function CatItem({ cat, active, highlight, onToggle, theme, indent = false }) {
  const primary = theme.primaryColor || "#111";
  return (
    <button
      type="button"
      onClick={() => onToggle(cat.id)}
      style={{
        width: "100%",
        minHeight: indent ? 36 : 42,
        border: 0,
        background: "transparent",
        padding: indent ? "6px 16px 6px 30px" : "6px 16px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: indent ? 14 : 15,
        lineHeight: 1.25,
        color: active ? primary : (highlight ? primary : "#4b4b4b"),
        fontWeight: active || highlight ? 600 : 400,
        textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      {capitalize(cat.nombre)}
    </button>
  );
}

// Versión mobile: chips horizontales con scroll
export const FiltrosCatalogoMobile = ({ categoriaIds = [], onSelect }) => {
  const { theme } = useClient();
  const [cats, setCats] = useState([]);
  const primary = theme.primaryColor || "#111";
  const badgeBg = pastelize(primary);

  useEffect(() => {
    api.catalogo.getCategorias()
      .then(res => setCats(res?.data || []))
      .catch(() => {});
  }, []);

  if (cats.length === 0) return null;

  const roots = cats.filter(c => !c.id_parent);
  const activeRootIds = [...new Set(cats
    .filter(c => categoriaIds.includes(c.id))
    .map(c => c.id_parent || c.id))];
  const sibs = cats.filter(c => c.id_parent && activeRootIds.includes(c.id_parent));

  const toggle = (id) => {
    onSelect(categoriaIds.includes(id)
      ? categoriaIds.filter(catId => catId !== id)
      : [...categoriaIds, id]
    );
  };

  const Chip = ({ id, label }) => {
    const active = id === null ? categoriaIds.length === 0 : categoriaIds.includes(id);
    return (
      <button
        onClick={() => id === null ? onSelect([]) : toggle(id)}
        style={{
          padding: "6px 14px", borderRadius: 20,
          border: `1.5px solid ${active ? pastelize(primary, 0.65) : "#ddd"}`,
          background: active ? badgeBg : "#fff",
          color: active ? primary : (theme.textColor || "#1a1a2e"),
          fontSize: 12, fontWeight: active ? 600 : 400,
          cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
        <Chip id={null} label="Todas" />
        {roots.map(r => <Chip key={r.id} id={r.id} label={capitalize(r.nombre)} />)}
      </div>
      {sibs.length > 0 && (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingLeft: 8 }}>
          {sibs.map(s => <Chip key={s.id} id={s.id} label={capitalize(s.nombre)} />)}
        </div>
      )}
    </div>
  );
};
